import { Color, Spacing } from '@persona-id/design-tokens';
import { StarFillIcon } from '@persona-id/design-tokens/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import { MonoSpan } from 'dashboard/components/Typography';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { TableLinkTR, TableTD } from 'dashboard/layouts/TableLayout';
import { ENHANCED_LIST_TYPES, LIST_TYPE_NAMES } from 'dashboard/lib/constants/list';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import Sticker from 'design-system/components/Sticker';
import ImageAvatar from 'images/dashboard/avatar.svg';
import { tForGovernmentId } from 'lib/governmentId';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

const FACE_PHOTO_HEIGHT = 114;
const FACE_PHOTO_WIDTH = 96;

const StyledSticker = styled(Sticker)`
  height: 20px;
  margin-left: ${Spacing[4]};
`;

interface ListMatchTableRowProps {
  list: List;
  listItem: ListItem;
  listMatch: ListMatch;
}

const ListMatchTableRow = ({ list, listItem, listMatch }: ListMatchTableRowProps) => {
  const { id } = listMatch;

  const { i18n } = useTranslation();

  const itemDisplay = (() => {
    switch (listItem.type) {
      case ApiModel.ListItemGovernmentIdNumber: {
        const idName = tForGovernmentId(
          i18n.getFixedT('en-US'),
          ['government-id.title'],
          'US',
          listItem.attributes['id-class'],
        );
        return `${listItem.attributes['id-number']} (${idName})`;
      }
      case ApiModel.ListItemIpAddress: {
        return listItem.attributes.value;
      }
      case ApiModel.ListItemName: {
        return [listItem.attributes['name-first'], listItem.attributes['name-last']]
          .filter(Boolean)
          .join(' ');
      }
      case ApiModel.ListItemPhoneNumber: {
        return listItem.attributes.value;
      }
      case ApiModel.ListItemEmailAddress: {
        return listItem.attributes.value;
      }
      case ApiModel.ListItemGeolocation: {
        // TODO: Andrew handle radius display units
        const latitude = listItem.attributes['latitude'];
        const longitude = listItem.attributes['longitude'];
        const radiusMeters = listItem.attributes['radius-meters'];

        if (latitude && longitude && radiusMeters) {
          return `${stringifyLatitude(latitude)}, ${stringifyLongitude(
            longitude,
          )} (${radiusMeters}m radius)`;
        }
        return <Sticker id={listItem.id}>Redacted</Sticker>;
      }
      case ApiModel.ListItemBrowserFingerprint: {
        return listItem.attributes.value;
      }
      case ApiModel.ListItemDeviceFingerprint: {
        return listItem.attributes.value;
      }
      case ApiModel.ListItemString: {
        return listItem.attributes.value;
      }
      case ApiModel.ListItemFace: {
        if (
          'face-photo' in listItem.attributes &&
          listItem.attributes['face-photo'] &&
          listItem.attributes['face-photo'].url
        ) {
          return (
            <AsyncImage
              height={`${FACE_PHOTO_HEIGHT}px`}
              width={`${FACE_PHOTO_WIDTH}px`}
              imageUrl={listItem.attributes['face-photo'].url}
              placeholder={<ImageAvatar height={FACE_PHOTO_HEIGHT} width={FACE_PHOTO_WIDTH} />}
              allowPreviewZoom={false}
            />
          );
        }
        return <Sticker id={listItem.id}>Redacted</Sticker>;
      }
      case ApiModel.ListItemCountry: {
        return listItem.attributes['country-code'];
      }
      case ApiModel.ListItemField: {
        return listItem.attributes['field-value'];
      }
      case ApiModel.ListItemSelfieBackground: {
        if (
          'selfie-photo' in listItem.attributes &&
          listItem.attributes['selfie-photo'] &&
          listItem.attributes['selfie-photo'].url
        ) {
          return (
            <AsyncImage
              height={`${FACE_PHOTO_HEIGHT}px`}
              width={`${FACE_PHOTO_WIDTH}px`}
              imageUrl={listItem.attributes['selfie-photo'].url}
              placeholder={<ImageAvatar height={FACE_PHOTO_HEIGHT} width={FACE_PHOTO_WIDTH} />}
              allowPreviewZoom={false}
            />
          );
        }
        return <Sticker id={listItem.id}>Redacted</Sticker>;
      }
      default:
        assertNever(listItem);
    }
  })();

  // Showing inquiry session's ID is not helpful so we show the inquiry token instead
  const sourceId =
    listMatch.relationships?.source?.data?.type === ApiModel.InquirySession
      ? listMatch.relationships['list-matchable'].data.id
      : listMatch.relationships?.source?.data?.id;

  return (
    <TableLinkTR
      to={`/dashboard/lists/${list.id}?filter=${JSON.stringify({ omni: listItem.id })}`}
      id={`list-matches__row--${id}`}
    >
      <TableTD id={list.id}>
        {list.attributes.name}
        {ENHANCED_LIST_TYPES.includes(list.type) && (
          <StyledSticker variant='primary' color={Color.PrimaryBlack} icon={<StarFillIcon />}>
            Enhanced
          </StyledSticker>
        )}
      </TableTD>
      <TableTD id={list.id}>{LIST_TYPE_NAMES[list.type]}</TableTD>
      <TableTD id={list.id}>{itemDisplay}</TableTD>
      <TableTD>
        {sourceId && (
          <HoverCopyableField copyValue={sourceId} confirmationText='Copied Source ID'>
            <MonoSpan>{sourceId}</MonoSpan>
          </HoverCopyableField>
        )}
      </TableTD>
    </TableLinkTR>
  );
};

export default ListMatchTableRow;
